<template>
<section class="">
  <div class="px-0 mx-auto">
    <div class="grid overflow-hidden grid-cols-1 md:grid-cols-12" :style="{ backgroundColor: props.bgColor }">
      <!-- Left: dark panel -->
      <div class="2xl:ml-[calc((100vw_-_1440px)_/_2)] lg:col-span-7 col-span-12 3xl:col-span-8 grid">
        <div class="pt-10 pr-6 pb-10 pl-6 my-auto md:pt-12 lg:pb-20 lg:pr-12 lg:pl-8 xl:pl-32">
          <!-- Top features -->
          <div class="flex gap-6 justify-between lg:max-w-xl 3xl:max-w-3xl">
            <div 
              v-for="(item, index) in props.items" 
              :key="index" 
              class="text-center"
            >
              <div class="flex justify-center items-center mx-auto h-16 md:h-20 xl:h-24">
                <nuxt-img 
                  :src="item.imageSrc" 
                  :width="item.imageWidth" 
                  :height="item.imageHeight" 
                  :alt="item.imageAlt"
                  format="webp"
                  loading="lazy"
                  sizes="(max-width: 768px) 114px, 114px"
                  class="object-contain w-auto h-full"
                />
              </div>
              <div class="mt-2 text-base text-white lg:mt-4 xl:text-xl md:text-lg font-ww-regular">{{ item.title }}</div>
            </div>
          </div>

          <!-- Copy -->
          <div class="mt-8 lg:max-w-2xl 3xl:max-w-3xl md:mt-20 xl:pr-6">
            <h2 class="text-3xl font-bold text-center text-white lg:leading-10 xl:text-42 md:text-4xl font-ww-bold lg:text-left" v-html="mainTitle"></h2>
            <div class="mt-6 text-white xl:text-xl md:text-lg tex-base font-ww-regular" v-html="description"></div>
          </div>
        </div>
      </div>

      <!-- Right: product image -->
      <div class="relative col-span-12 lg:col-span-5 3xl:col-span-4">
        <nuxt-img 
          :src="rightImageSrc" 
          :width="rightImageWidth" 
          :height="rightImageHeight" 
          class="hidden object-cover mx-auto w-full max-w-xl h-full lg:max-w-full lg:mx-0 lg:block" 
          :alt="rightImageAlt"
          format="webp"
          loading="lazy"
          sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 40vw"
        />

        <nuxt-img
          :src="props.mobileImageSrc" 
          class="object-cover mx-auto w-full h-full lg:max-w-auto lg:mx-0 lg:hidden" 
          :alt="props.mobileImageAlt"
          format="webp"
          loading="lazy"
          sizes="(max-width: 768px) 100vw, (max-width: 1024px) 100vw, 40vw"
        />
      
      </div>
    </div>
  </div>
</section>
</template>

<script setup lang="ts">

// Props
const props = defineProps({
  bgColor: {
    type: String,
    default: '#393735'
  },
  items: {
    type: Array,
    default: () => [
      {
        imageSrc: '/assets/images/beans-cog-1.png',
        imageWidth: 90,
        imageHeight: 101,
        imageAlt: 'Low Acid Beans',
        title: 'Low Acid Beans'
      },
      {
        imageSrc: '/assets/images/beans-cog-2.png',
        imageWidth: 90,
        imageHeight: 101,
        imageAlt: 'Subscribe & Save',
        title: 'Subscribe & Save 30%+'
      },
      {
        imageSrc: '/assets/images/beans-cog-3.png',
        imageWidth: 90,
        imageHeight: 101,
        imageAlt: 'Free Shipping',
        title: 'Free Shipping'
      }
    ]
  },
  mainTitle: {
    type: String,
    default: 'The Love Your Coffee<br/>Guarantee'
  },
  description: {
    type: String,
    default: `Coffee is personal. The source of beans and roasting process make each coffee unique.
<br><br>
At Lifeboost, we want you to love your coffee so you realize the long-term benefits of drinking our clean, low acid, non-GMO, single-origin coffee.
<br><br>
If you don't absolutely love your Lifeboost Coffee product, let us know within 30 days of your order and we'll exchange it for free or give you a full refund.
<br><br>
We encourage you to open a bag of our coffee, take in the aroma, make a cup, and try it for yourself. If you don't love it, even if the bag is opened, we'll help you exchange it for a roast or flavor you might like better or give you a refund.
<br><br>
The fastest way to get help with an exchange is to call us at 1-800-479-1596. Or, you can email us at support@lifeboostcoffee.com and we'll help you with your order.`
  },
  rightImageSrc: {
    type: String,
    default: '/assets/images/cognition-right-cut.png'
  },
  rightImageWidth: {
    type: Number,
    default: 1284
  },
  rightImageHeight: {
    type: Number,
    default: 1920
  },
  rightImageAlt: {
    type: String,
    default: 'Lifeboost Cognition coffee bag with espresso'
  },
  mobileImageSrc: {
    type: String,
    default: '/assets/images/cognition-mobile-container.png'
  },
  mobileImageAlt: {
    type: String,
    default: 'Lifeboost Cognition mobile container'
  }
})

// Builder.io component definition
defineBuilderComponent({
  friendlyName: 'Icons Text With Visual Section',
  inputs: [
    {
      name: 'bgColor',
      type: 'color',
      defaultValue: '#393735'
    },
    {
      name: 'items',
      type: 'list',
      subFields: [
        {
          name: 'imageSrc',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg', 'webp'],
          required: true
        },
        {
          name: 'imageWidth',
          type: 'number',
          defaultValue: 90
        },
        {
          name: 'imageHeight',
          type: 'number',
          defaultValue: 101
        },
        {
          name: 'imageAlt',
          type: 'text',
          required: true
        },
        {
          name: 'title',
          type: 'text',
          required: true
        }
      ],
      defaultValue: [
        {
          imageSrc: '/assets/images/beans-cog-1.png',
          imageWidth: 90,
          imageHeight: 101,
          imageAlt: 'Low Acid Beans',
          title: 'Low Acid Beans'
        },
        {
          imageSrc: '/assets/images/beans-cog-2.png',
          imageWidth: 90,
          imageHeight: 101,
          imageAlt: 'Subscribe & Save',
          title: 'Subscribe & Save 30%+'
        },
        {
          imageSrc: '/assets/images/beans-cog-3.png',
          imageWidth: 90,
          imageHeight: 101,
          imageAlt: 'Free Shipping',
          title: 'Free Shipping'
        }
      ]
    },
    {
      name: 'mainTitle',
      type: 'richText',
      defaultValue: 'The Love Your Coffee<br/>Guarantee'
    },
    {
      name: 'description',
      type: 'richText',
      defaultValue: `Coffee is personal. The source of beans and roasting process make each coffee unique.
<br><br>
At Lifeboost, we want you to love your coffee so you realize the long-term benefits of drinking our clean, low acid, non-GMO, single-origin coffee.
<br><br>
If you don't absolutely love your Lifeboost Coffee product, let us know within 30 days of your order and we'll exchange it for free or give you a full refund.
<br><br>
We encourage you to open a bag of our coffee, take in the aroma, make a cup, and try it for yourself. If you don't love it, even if the bag is opened, we'll help you exchange it for a roast or flavor you might like better or give you a refund.
<br><br>
The fastest way to get help with an exchange is to call us at 1-800-479-1596. Or, you can email us at support@lifeboostcoffee.com and we'll help you with your order.`
    },
    {
      name: 'rightImageSrc',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg', 'webp'],
      defaultValue: '/assets/images/cognition-right-cut.png'
    },
    {
      name: 'rightImageWidth',
      type: 'number',
      defaultValue: 1284
    },
    {
      name: 'rightImageHeight',
      type: 'number',
      defaultValue: 1920
    },
    {
      name: 'rightImageAlt',
      type: 'text',
      defaultValue: 'Lifeboost Cognition coffee bag with espresso'
    },
    {
      name: 'mobileImageSrc',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg', 'webp'],
      defaultValue: '/assets/images/cognition-mobile-container.png'
    },
    {
      name: 'mobileImageAlt',
      type: 'text',
      defaultValue: 'Lifeboost Cognition mobile container'
    }
  ]
})
</script>